import { MutationResult } from "@/api/utils/types"
import { useMutation } from "@/api/utils/useQuery"
import { NewsfeedMutations } from "@/api/newsfeed/mutations"
import Cache from "@/api/utils/Cache"
import type {
  CreateArticlePayload,
  UpdateArticlePayload,
} from "./NewsfeedMutator.types"
import type { IArticleRaw } from "@/models/newsfeed/newsfeed.parser"
import type { IImageUploadResponse } from "@/models/newsfeed/newsfeed.types"

export interface INewsfeedMutator {
  createArticle(
    onSuccessFn?: (response: IArticleRaw) => void,
    onErrorFn?: (error: unknown) => void,
  ): MutationResult<CreateArticlePayload, IArticleRaw>

  updateArticle(
    onSuccessFn?: (response: IArticleRaw) => void,
    onErrorFn?: (error: unknown) => void,
  ): MutationResult<UpdateArticlePayload, IArticleRaw>

  deleteArticle(
    onSuccessFn?: () => void,
    onErrorFn?: (error: unknown) => void,
  ): MutationResult<string, void>

  publishArticle(
    onSuccessFn?: (response: IArticleRaw) => void,
    onErrorFn?: (error: unknown) => void,
  ): MutationResult<string, IArticleRaw>

  unpublishArticle(
    onSuccessFn?: (response: IArticleRaw) => void,
    onErrorFn?: (error: unknown) => void,
  ): MutationResult<string, IArticleRaw>

  uploadImage(
    onSuccessFn?: (response: IImageUploadResponse) => void,
    onErrorFn?: (error: unknown) => void,
  ): MutationResult<File, IImageUploadResponse>
}

class NewsfeedMutatorClass implements INewsfeedMutator {
  createArticle(
    onSuccessFn?: (response: IArticleRaw) => void,
    onErrorFn?: (error: unknown) => void,
  ) {
    return useMutation({
      ...NewsfeedMutations.article.create(),
      serializerFn: (data: CreateArticlePayload) => data,
      onSuccessFn: (response: IArticleRaw) => {
        Cache.invalidate(["articles"])
        Cache.invalidate(["admin"])
        onSuccessFn?.(response)
      },
      onErrorFn,
    })
  }

  updateArticle(
    onSuccessFn?: (response: IArticleRaw) => void,
    onErrorFn?: (error: unknown) => void,
  ) {
    return useMutation({
      ...NewsfeedMutations.article.update(),
      serializerFn: (data: UpdateArticlePayload) => data,
      onSuccessFn: (response: IArticleRaw) => {
        Cache.invalidate(["articles"])
        Cache.invalidate(["admin"])
        onSuccessFn?.(response)
      },
      onErrorFn,
    })
  }

  deleteArticle(
    onSuccessFn?: () => void,
    onErrorFn?: (error: unknown) => void,
  ) {
    return useMutation({
      ...NewsfeedMutations.article.delete(),
      serializerFn: (id: string) => id,
      onSuccessFn: () => {
        Cache.invalidate(["articles"])
        Cache.invalidate(["admin"])
        onSuccessFn?.()
      },
      onErrorFn,
    })
  }

  publishArticle(
    onSuccessFn?: (response: IArticleRaw) => void,
    onErrorFn?: (error: unknown) => void,
  ) {
    return useMutation({
      ...NewsfeedMutations.article.publish(),
      serializerFn: (id: string) => id,
      onSuccessFn: (response: IArticleRaw) => {
        Cache.invalidate(["articles"])
        Cache.invalidate(["admin"])
        onSuccessFn?.(response)
      },
      onErrorFn,
    })
  }

  unpublishArticle(
    onSuccessFn?: (response: IArticleRaw) => void,
    onErrorFn?: (error: unknown) => void,
  ) {
    return useMutation({
      ...NewsfeedMutations.article.unpublish(),
      serializerFn: (id: string) => id,
      onSuccessFn: (response: IArticleRaw) => {
        Cache.invalidate(["articles"])
        Cache.invalidate(["admin"])
        onSuccessFn?.(response)
      },
      onErrorFn,
    })
  }

  uploadImage(
    onSuccessFn?: (response: IImageUploadResponse) => void,
    onErrorFn?: (error: unknown) => void,
  ) {
    return useMutation({
      ...NewsfeedMutations.image.upload(),
      serializerFn: (file: File) => file,
      onSuccessFn: (response: IImageUploadResponse) => {
        onSuccessFn?.(response)
      },
      onErrorFn,
      invalidateCache: false,
    })
  }
}

export const NewsfeedMutator = new NewsfeedMutatorClass()
